import React, { useState, memo } from 'react';
import { components, type ControlProps, type GroupBase } from 'react-select';
import BasicSelect, { BareSelect } from './styles';

export interface SelectOption {
  value: string;
  label: string;
}

export interface CustomSelectProps {
  options: SelectOption[];
  value?: SelectOption | null;
  onChange: (value: SelectOption | null) => void;
  variant?: 'basic' | 'minimal' | 'bare';
  placeholder?: string;
  innerRef?: React.Ref<any>;
  id?: string;
  isSearchable?: boolean;
  defaultValue?: SelectOption | null;
  isLoading?: boolean;
  /**
   * Render the dropdown menu in a portal at document.body (menuPosition fixed)
   * so it escapes `overflow: hidden/auto` ancestors (e.g. a scrolling card
   * body or a `MainSection` with `overflow: hidden`). Opt-in — consumers that
   * portal must also provide global `.select__menu-portal` styling, since the
   * styled-components menu rules are scoped to the in-tree wrapper. See
   * `ParticipantStatsFilter`.
   */
  menuPortal?: boolean;
  /**
   * Accessible name for the select input. A `placeholder` alone is not an
   * accessible name — once a value is picked, a screen reader announces the
   * value with no indication of what it controls.
   */
  ariaLabel?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  options,
  value,
  onChange,
  variant = 'basic',
  ...props
}) => {
  const [uniqueId] = useState(
    () => 'select_' + Math.random().toFixed(5).slice(2),
  );

  const Control = ({ children, ...controlProps }: ControlProps<any, any, GroupBase<any>>) => {
    // `bare` variant skips the trailing caret/chevron icon — consumers render
    // their own chevron as a sibling so it can sit inside their custom chrome.
    if (variant === 'bare') {
      return <components.Control {...controlProps}>{children}</components.Control>;
    }
    return (
      <components.Control className={variant === 'basic' ? 'field-input' : ''} {...controlProps}>
        {children}
        {variant === 'minimal' ? (
          <i className="icon icon-caret-down" />
        ) : (
          <i className="icon icon-chevron-down" />
        )}
      </components.Control>
    );
  };

  const selectId = props.id || uniqueId;

  const selectProps: Record<string, any> = {
    classNamePrefix: 'select',
    id: selectId,
    // Typing opens the virtual keyboard on phones — opt in only for long lists.
    isSearchable: props.isSearchable ?? false,
    'aria-label': props.ariaLabel,
    options,
    value,
    onChange,
    placeholder: props.placeholder,
    openMenuOnFocus: true,
    blurInputOnSelect: true,
    ref: props.innerRef,
    defaultValue: props.defaultValue,
    isLoading: props.isLoading,
    loadingMessage: () => 'Loading...',
    onMenuClose: () => {
      const menuEl = document.querySelector(`#${selectId} .select__menu`);
      const containerEl = menuEl?.parentElement;
      const clonedMenuEl = menuEl?.cloneNode(true) as HTMLElement | undefined;

      if (!clonedMenuEl) return;

      clonedMenuEl.classList.add('close');
      clonedMenuEl.addEventListener('animationend', () => {
        containerEl?.removeChild(clonedMenuEl);
      });

      containerEl?.appendChild(clonedMenuEl);
    },
    components: {
      Control,
    },
  };

  // Opt-in portal: render the menu at document.body so it isn't clipped by
  // overflow ancestors. menuPosition 'fixed' keeps it anchored to the control
  // while scrolling; zIndex is set inline (no `!important` needed) because the
  // styled-components menu rules don't reach a portaled menu.
  if (props.menuPortal) {
    selectProps.menuPortalTarget = document.body;
    selectProps.menuPosition = 'fixed';
    selectProps.styles = {
      menuPortal: (base: Record<string, any>) => ({ ...base, zIndex: 9999 }),
    };
  }

  const SelectComponent = variant === 'bare' ? BareSelect : BasicSelect;
  return <SelectComponent {...selectProps} />;
};

export default memo(CustomSelect);
